import {ParamComponent} from 'panel';
import isEqual from 'lodash/isEqual';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {parseNumber} from 'common/util';

import {
  DefaultMultiAttributionLookbackDays,
  MaxMultiAttributionLookbackDays,
  MinMultiAttributionLookbackDays,
} from './constants';
import {MultiAttributionLookbackCancelEvent, MultiAttributionLookbackChangeEvent} from './types';
import {template} from './template';
import css from './index.inline.styl';

type MultiAttributionLookbackState = {
  stagedLookbackDays: string;
};

type MultiAttributionLookbackParams = {
  lookbackDays: number;
  projectId: number;
  handleCancel?: (ev: MultiAttributionLookbackCancelEvent) => void;
  handleChange?: (ev: MultiAttributionLookbackChangeEvent) => void;
};

type MultiAttributionLookbackJsxProps = {
  params?: MultiAttributionLookbackParams;
};

export class MultiAttributionLookback extends ParamComponent<
  MultiAttributionLookbackParams,
  MultiAttributionLookbackState
> {
  get config() {
    return Object.assign(super.config, {
      css,
      template,
      defaultState: {},
      useShadowDom: true,
      params: {
        lookbackDays: {
          type: Object,
          required: true,
        },
        projectId: Number,
        handleCancel: Function,
        handleChange: Function,
      },
      hooks: {
        preUpdate: (_newState, newParams?: MultiAttributionLookbackParams) => {
          if (newParams && !isEqual(newParams.lookbackDays, this.params.lookbackDays)) {
            this.handleParamsUpdate(newParams);
          }
        },
      },
    });
  }

  connectedCallback() {
    this.handleParamsUpdate(this.params);
    super.connectedCallback();
  }

  handleParamsUpdate(params: MultiAttributionLookbackParams): void {
    this.update({
      stagedLookbackDays: params.lookbackDays.toString(),
    });
  }

  getMaxLookbackDays(): number {
    const PROJECT_LIMITS = {
      '2718323': 30, // crunchyroll
      '2620273': 30, // zalora
    };

    return PROJECT_LIMITS[this.params.projectId] ?? MaxMultiAttributionLookbackDays;
  }

  parseStagedLookbackDays(): number | undefined {
    return parseNumber(this.state.stagedLookbackDays);
  }

  getInvalidLookbackText(): string | undefined {
    const maxDays = this.getMaxLookbackDays();
    const value = this.parseStagedLookbackDays();
    if (value >= MinMultiAttributionLookbackDays && value <= maxDays) {
      return undefined;
    }
    return `Lookback Windows up to ${maxDays} days are supported. Please choose a value within the range to generate a result.`;
  }

  handleLookbackDaysInput(ev): void {
    this.update({stagedLookbackDays: ev.target.value});
  }

  isDefaultLookbackDays(): boolean {
    return this.parseStagedLookbackDays() === DefaultMultiAttributionLookbackDays;
  }

  handleClickReset() {
    this.update({stagedLookbackDays: DefaultMultiAttributionLookbackDays.toString()});
  }

  dispatchChange() {
    const event: MultiAttributionLookbackChangeEvent = new CustomEvent(`change`, {
      detail: {lookbackDays: this.parseStagedLookbackDays()},
    });
    this.params?.handleChange(event);
  }

  dispatchCancel() {
    const event: MultiAttributionLookbackCancelEvent = new CustomEvent(`cancel`, {});
    this.params?.handleCancel(event);
  }
}

export const MultiAttributionLookbackEl = defineAndMakeJsxFactory<MultiAttributionLookbackJsxProps>(
  `mp-multi-attribution-lookback`,
  MultiAttributionLookback,
);
export const MultiAttributionLookbackReact = panel2react<MultiAttributionLookbackJsxProps>(
  `mp-multi-attribution-lookback`,
  MultiAttributionLookback,
);
